import { useState } from "react";
import { Button } from "@/app/components/system/Button";
import { Field } from "@/app/components/Field";

interface AuthFormProps {
  submitLabel: string;
  busyLabel: string;
  minPasswordLength: number;
  onSubmit: (email: string, password: string) => Promise<void>;
  footer: React.ReactNode;
  /** Optional content rendered between the form and footer (e.g. forgot-password link). */
  aside?: React.ReactNode;
}

/**
 * Email/password form used by login and signup screens.
 * Owns field state, submit/busy state, and inline error display.
 */
export function AuthForm({
  submitLabel, busyLabel, minPasswordLength, onSubmit, footer, aside,
}: AuthFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailLooksValid = /^\S+@\S+\.\S+$/.test(email.trim());

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setBusy(false);
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit} noValidate className="auth-form">
        <Field label="Email" required>
          <input
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>
        <Field label="Password" required>
          <input
            type="password"
            autoComplete={submitLabel === "Create account" ? "new-password" : "current-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={minPasswordLength}
            required
          />
        </Field>
        {aside}

        {error && (
          <p className="m-0 mt-1 mb-3 text-[12.5px] text-[oklch(0.5_0.14_28)] font-medium">{error}</p>
        )}

        <Button
          type="submit"
          disabled={busy || !emailLooksValid || password.length < minPasswordLength}
          className="w-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? busyLabel : submitLabel}
        </Button>
      </form>

      <p className="auth-footer">{footer}</p>
    </>
  );
}
